import type { User } from "firebase/auth";
import {
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import type { AssetDamage, DamageCategory } from "../types/imobi";
import { db } from "./firebase";
import { enqueueOfflineChange } from "./localDb";

export type CreateAssetDamageInput = {
  id?: string;
  companyId: string;
  assetId: string;
  externalServiceId?: string;
  externalServiceItemId?: string;
  inspectionId?: string;
  description: string;
  category: DamageCategory;
  photoUrls: string[];
  preexisting: boolean;
  notes?: string;
};

export type OfflineDamagePhotoPayload = {
  companyId: string;
  assetId: string;
  damageId: string;
  file: File;
};

export function generateDamageId(companyId: string, assetId: string): string {
  if (!db) {
    return `local-${Date.now()}`;
  }

  return doc(collection(db, "companies", companyId, "assets", assetId, "damages")).id;
}

export async function queueOfflineDamagePhoto(payload: OfflineDamagePhotoPayload): Promise<void> {
  await enqueueOfflineChange({
    id: `damagePhoto-${payload.damageId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    entity: "damagePhoto",
    action: "create",
    payload,
    createdAt: new Date().toISOString(),
    attempts: 0
  });
}

export async function attachDamagePhoto(companyId: string, assetId: string, damageId: string, url: string, user: User): Promise<void> {
  if (!db) {
    return;
  }

  await updateDoc(doc(db, "companies", companyId, "assets", assetId, "damages", damageId), {
    photoUrls: arrayUnion(url),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function userName(user: User) {
  return user.displayName ?? user.email ?? user.uid;
}

function mapDamage(companyId: string, assetId: string, id: string, data: Record<string, unknown>): AssetDamage {
  return {
    id,
    companyId,
    assetId,
    externalServiceId: data.externalServiceId ? String(data.externalServiceId) : undefined,
    externalServiceItemId: data.externalServiceItemId ? String(data.externalServiceItemId) : undefined,
    inspectionId: data.inspectionId ? String(data.inspectionId) : undefined,
    identifiedByUserName: String(data.identifiedByUserName ?? ""),
    description: String(data.description ?? ""),
    category: (data.category ?? "outro") as DamageCategory,
    status: (data.status ?? "aberta") as AssetDamage["status"],
    photoUrls: Array.isArray(data.photoUrls) ? data.photoUrls.map(String) : [],
    preexisting: data.preexisting === true,
    notes: data.notes ? String(data.notes) : undefined,
    resolvedAt: serializeTimestamp(data.resolvedAt) || undefined,
    resolvedByUserName: data.resolvedByUserName ? String(data.resolvedByUserName) : undefined,
    resolutionNotes: data.resolutionNotes ? String(data.resolutionNotes) : undefined,
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? "")
  };
}

export function subscribeAssetDamages(companyId: string | undefined, assetId: string | undefined, onChange: (damages: AssetDamage[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId || !assetId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "assets", assetId, "damages"), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((damageDoc) => mapDamage(companyId, assetId, damageDoc.id, damageDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function createAssetDamage(input: CreateAssetDamageInput, user: User): Promise<string> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.description.trim()) {
    throw new Error("Descreva a avaria identificada.");
  }

  const damageRef = input.id
    ? doc(db, "companies", input.companyId, "assets", input.assetId, "damages", input.id)
    : doc(collection(db, "companies", input.companyId, "assets", input.assetId, "damages"));
  const now = serverTimestamp();
  const batch = writeBatch(db);

  batch.set(damageRef, {
    externalServiceId: input.externalServiceId ?? null,
    externalServiceItemId: input.externalServiceItemId ?? null,
    inspectionId: input.inspectionId ?? null,
    identifiedByUserName: userName(user),
    description: input.description.trim(),
    category: input.category,
    status: "aberta",
    photoUrls: input.photoUrls,
    preexisting: input.preexisting,
    notes: input.notes?.trim() || null,
    createdAt: now,
    createdBy: user.uid
  });

  if (input.externalServiceId) {
    const eventRef = doc(collection(db, "companies", input.companyId, "externalServices", input.externalServiceId, "events"));
    batch.set(eventRef, {
      type: "divergencia_registrada",
      userId: user.uid,
      userName: userName(user),
      description: `Avaria registrada: ${input.description.trim()}`,
      assetId: input.assetId,
      participantUserId: null,
      createdAt: now
    });
  }

  await batch.commit();
  return damageRef.id;
}

export async function resolveAssetDamage(companyId: string, assetId: string, damageId: string, resolutionNotes: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await updateDoc(doc(db, "companies", companyId, "assets", assetId, "damages", damageId), {
    status: "resolvida",
    resolvedAt: serverTimestamp(),
    resolvedByUserName: userName(user),
    resolutionNotes: resolutionNotes.trim() || null,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function markAssetDamageEscalatedToMaintenance(companyId: string, assetId: string, damageId: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await updateDoc(doc(db, "companies", companyId, "assets", assetId, "damages", damageId), {
    status: "encaminhada_manutencao",
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}
